import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { FreeTrailPeriodBadge } from 'hb-react/shared/apps/Memberships/components/FreeTrialPeriodBadge';
import { translations } from 'hb-react/shared/apps/Memberships/utils/translations';
import { Price } from 'hb-react/shared/components/Price';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import decodeString from 'hb-react/shared/utils/decodeString';

import FixedEndWarning from '../FixedEndWarning';

const SinglePrice = ({ paymentOptions = [], price, currency }) => {
  if (paymentOptions.length === 1) {
    const [option] = paymentOptions;

    return (
      <Price
        price={option.price}
        currency={option.currency}
        hasFreeTrial={option.has_free_trial}
      />
    );
  }

  return <Price price={price} currency={currency} />;
};

const CardHeader = ({ membership, withMultiplePaymentOptions }) => {
  const breakpoint = useBreakpoint();
  const {
    title,
    description,
    image,
    logo,
    price,
    currency,
    periodicity,
    validity,
    enable_expire_month: enableExpireMonth = false,
    expire_month: expireMonth,
    payment_options: paymentOptions = [],
    free_trial_period_periodicity: freeTrialPeriodPeriodicity,
  } = membership;

  const { flagEvGsMembershipLogo } = useFeatureFlag();

  const membershipTypeLogo =
    flagEvGsMembershipLogo && logo ? logo.thumbnail_url : image;

  const hasFreeTrial =
    paymentOptions?.[0]?.has_free_trial || membership.has_free_trial;

  return (
    <Box>
      {membershipTypeLogo && (
        <Box alignItems="flex-start" mr={4} flexGrow={0} flexShrink={0}>
          <ImageWrapper
            width={{ _: '24px', l: '30px' }}
            height={{ _: '24px', l: '30px' }}
          >
            <Image
              src={membershipTypeLogo}
              alt={s__('Memberships|Membership picture')}
              width="100%"
            />
          </ImageWrapper>
        </Box>
      )}
      <Box flexDirection={description ? 'column' : 'row'} width="100%">
        <Box
          flexGrow={1}
          alignItems="center"
          maxWidth={453}
          height={{ _: '24px', m: '30px' }}
        >
          <Title>{title}</Title>
        </Box>
        {!withMultiplePaymentOptions && enableExpireMonth && (
          <Box mb={2} mt={2}>
            <FixedEndWarning validity={validity} expireMonth={expireMonth} />
          </Box>
        )}
        <Box
          mt={2}
          flexDirection={breakpoint === 's' ? 'column' : 'row'}
          justifyContent="space-between"
        >
          <DescriptionContainer
            className="ckeditor-content--reset"
            dangerouslySetInnerHTML={{ __html: decodeString(description) }}
            display="block"
            maxWidth="453px"
            flex="1 1 auto"
            pr={4}
          />
          {!withMultiplePaymentOptions && (
            <Box flexDirection="row" alignItems="flex-start" flex="1 0 auto">
              <Box>{hasFreeTrial && <FreeTrailPeriodBadge mt={0} />}</Box>
              <Box flexDirection="column" alignItems="flex-start">
                <Box font="h4-semibold">
                  {hasFreeTrial && (
                    <TextEllipsis>
                      {`${s__('Memberships|Free')} / ${n__(
                        '%{days} day',
                        '%{days} days',
                        freeTrialPeriodPeriodicity,
                      ).i__({
                        days: freeTrialPeriodPeriodicity,
                      })}`}
                    </TextEllipsis>
                  )}
                </Box>
                <Box font="h4-semibold">
                  <SinglePrice
                    price={price}
                    currency={currency}
                    paymentOptions={paymentOptions}
                  />
                  <>&nbsp;{`/ ${translations.periodicity[periodicity]}`}</>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CardHeader;

const ImageWrapper = styled(Box)`
  border-radius: 50%;
  overflow: hidden;
`;

const Title = styled(Box).attrs(() => ({
  fontSize: { _: 5 },
  fontWeight: 1,
}))``;

const TextEllipsis = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const DescriptionContainer = styled(Box)`
  word-break: break-word;
`;
